import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import Layout from '../components/layout/Layout';
import Input from '../components/common/Input';
import ResponsiveTable from '../components/common/ResponsiveTable';
import { useAuth } from '../contexts/AuthContext';
import { useClients } from '../hooks/useClients';
import { gateEntryService } from '../services/gateEntryService';
import { dispatchService } from '../services/dispatchService';
import { useGateEntrySummary } from '../hooks/useGateEntrySummary';
import { Client } from '../types';
import { useDebounce } from '../hooks/useDebounce';
import { INPUT_SANITIZERS, FIELD_VALIDATORS, VALIDATION_PATTERNS } from '../utils/validationHelpers';
import { useToast } from '../contexts/ToastContext';
import api from '../services/api';

// BE/Inv Date defaults to today but stays editable.
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function SecurityCheck() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const clientDropdownRef = useRef<HTMLDivElement>(null);
  
  // Vehicle Assignment Form State
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState<any | null>(null);
  const [vehicleFormData, setVehicleFormData] = useState({
    vehicleNumber: '',
    driverName: '',
    driverPhone: '',
    remarks: '',
    invDate: todayStr(),
  });
  const [vehicleFormErrors, setVehicleFormErrors] = useState<Record<string, string>>({});
  
  // View Outward Details Modal State
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewEntry, setViewEntry] = useState<any | null>(null);

  // View Inward Details Modal State
  const [showInwardModal, setShowInwardModal] = useState(false);
  const [viewInwardEntry, setViewInwardEntry] = useState<any | null>(null);

  // Fetch dispatch items for the entry being viewed
  const { data: viewDispatchItems, isLoading: viewItemsLoading } = useQuery({
    queryKey: ['dispatch', viewEntry?.dispatchId, 'items'],
    queryFn: () => dispatchService.getDispatchItems(viewEntry!.dispatchId),
    enabled: !!showViewModal && !!viewEntry?.dispatchId,
  });
  
  // Fetch gate entry summary from API
  const { data: summaryResponse, isLoading: summaryLoading } = useGateEntrySummary();
  const summary = summaryResponse?.data || {
    totalEntries: 0,
    inwardShipments: 0,
    outwardShipments: 0,
    todaysEntries: 0,
  };
  
  // Fetch gate entries with infinite scroll (5 items per page)
  const {
    data: gateEntriesData,
    error: gateEntriesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['gateEntries', 'infinite'],
    queryFn: ({ pageParam = 1 }) => 
      gateEntryService.getGateEntries({ page: pageParam, limit: 20 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const meta = (lastPage as any)?.data?.meta;
      if (!meta) return undefined;
      return meta.page < meta.totalPages ? meta.page + 1 : undefined;
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  // Flatten all pages of data and show latest entries first
  const gateEntries = useMemo(() => {
    if (!gateEntriesData) return [];
    const items = gateEntriesData.pages.flatMap(page => (page as any)?.data?.items || []);
    return [...items].sort((a, b) => {
      const aTime = new Date(a.createdAt || a.entryDate).getTime();
      const bTime = new Date(b.createdAt || b.entryDate).getTime();
      return bTime - aTime;
    });
  }, [gateEntriesData]);

  // Debounce search term for API calls
  const debouncedSearchTerm = useDebounce(clientSearchTerm, 300);
  
  // Fetch clients when dropdown is open or when searching
  const { data: clientsResponse, isLoading: clientsLoading, error: clientsError } = useClients(
    debouncedSearchTerm.trim()
      ? { search: debouncedSearchTerm.trim(), status: ['active'] }
      : { status: ['active'] },
    {
      enabled: showClientDropdown, // Fetch when dropdown is open
    }
  );
  
  const clients = clientsResponse?.data || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
        setShowClientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle client selection
  const handleClientSelect = (client: Client) => {
    if (!client?.id) {
      console.error('Invalid client selected:', client);
      return;
    }
    
    setSelectedClient(client);
    setFormData({ ...formData, clientId: client.id });
    setClientSearchTerm(''); // Clear search after selection
    setShowClientDropdown(false);
  };

  // Handle search input change
  const handleClientSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setClientSearchTerm(value);
    
    // Show dropdown when user starts typing or clear search
    if (!showClientDropdown) {
      setShowClientDropdown(true);
    }
  };

  // Handle keyboard events
  const handleClientSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowClientDropdown(false);
      setClientSearchTerm('');
    }
  };

  // Clear all validation errors when opening form (Client Onboarding pattern)
  const resetForm = () => {
    setFormData({
      warehouseId: user?.activeWarehouseId || 0,
      vehicleNumber: '',
      driverName: '',
      driverPhone: '',
      clientId: 0,
      shipmentType: 'inward',
      expectedQuantity: 0,
      remarks: '',
      invoiceNumber: '',
      ewayBillNumber: '',
      packingSlipNumber: '',
      invDate: todayStr(),
    });
    setClientSearchTerm('');
    setSelectedClient(null);
    setShowClientDropdown(false);
    setErrors({});
    setError(null);
  };

  // Create gate entry mutation
  const createGateEntryMutation = useMutation({
    mutationFn: gateEntryService.createGateEntry,
    onSuccess: (response) => {
      // Refresh gate entries list and summary
      queryClient.invalidateQueries({ queryKey: ['gateEntries'] });
      queryClient.invalidateQueries({ queryKey: ['gateEntrySummary'] });
      setShowForm(false);
      setError(null);
      
      // Reset form and clear all errors/states
      resetForm();
      const gateEntryNumber = response.data?.entryNumber || 
        `GIN-${new Date().getFullYear()}-${String(response.data?.id || Date.now()).padStart(5, '0')}`;
      showToast(
        `Inbound Gate Entry Created! Entry #${gateEntryNumber} for ${formData.vehicleNumber} (${formData.driverName}) has been registered. Navigate to Inbound module for GRN processing.`,
        'success'
      );
    },
    onError: (error: any) => {
      setError(error?.message || 'Failed to create gate entry');
    },
  });
  
  // Assign vehicle to outward gate entry mutation
  const assignVehicleMutation = useMutation({
    mutationFn: async ({ gateEntryId, vehicleData }: { gateEntryId: number; vehicleData: any }) => {
      console.log('Starting vehicle assignment for gate entry:', gateEntryId);
      
      // First, update the gate entry with vehicle details
      const gateEntryResponse = await api.patch(`/gate-entries/${gateEntryId}`, vehicleData);
      console.log('Gate entry updated successfully:', gateEntryResponse);
      
      // Then, if there's an associated dispatch, approve it for security (this will deduct inventory)
      try {
        // Get the gate entry to find associated dispatch
        console.log('Fetching gate entry to check for dispatch...');
        const gateEntryResponse = await gateEntryService.getGateEntryById(gateEntryId.toString());
        const gateEntry = gateEntryResponse.data; // Extract actual gate entry data from response
        console.log('Gate entry data:', gateEntry);
        
        if (gateEntry.dispatchId) {
          console.log('Found dispatch ID:', gateEntry.dispatchId, 'triggering security approval...');
          // Security approve the dispatch (this triggers inventory deduction and transaction ledger)
          const securityApprovalResponse = await dispatchService.securityApproveDispatch(gateEntry.dispatchId);
          console.log('Security approval completed successfully:', securityApprovalResponse);
        } else {
          console.log('No dispatch found for this gate entry');
        }
      } catch (dispatchError: any) {
        console.error('Dispatch security approval failed:', dispatchError);
        // Don't throw here - gate entry was successfully updated
        // This is just a warning in case dispatch doesn't exist or isn't in security_pending state
        throw new Error(`Vehicle assigned but dispatch approval failed: ${dispatchError.message || dispatchError}`);
      }
      
      return gateEntryResponse;
    },
    onSuccess: () => {
      console.log('Vehicle assignment completed successfully, invalidating queries...');
      queryClient.invalidateQueries({ queryKey: ['gateEntries'] });
      queryClient.invalidateQueries({ queryKey: ['gateEntrySummary'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] }); // Invalidate reports since new data will appear
      queryClient.invalidateQueries({ queryKey: ['dispatches'] }); // Invalidate dispatches list
      setShowVehicleForm(false);
      setSelectedDispatch(null);
      resetVehicleForm();
      showToast('Vehicle assigned and outward process completed successfully! Entry will now appear in reports.', 'success');
    },
    onError: (error: any) => {
      console.error('Vehicle assignment failed:', error);
      showToast(error?.message || 'Failed to assign vehicle', 'error');
    },
  });
  
  const [formData, setFormData] = useState({
    warehouseId: user?.activeWarehouseId || 0,
    vehicleNumber: '',
    driverName: '',
    driverPhone: '',
    clientId: 0,
    shipmentType: 'inward' as 'inward',
    // Common fields
    expectedQuantity: 0,
    remarks: '',
    // Inward specific fields
    invoiceNumber: '',
    ewayBillNumber: '',
    packingSlipNumber: '',
    invDate: todayStr(),
  });

  // Update warehouseId when user's activeWarehouseId changes
  useEffect(() => {
    if (user?.activeWarehouseId) {
      setFormData((prev) => ({
        ...prev,
        warehouseId: user.activeWarehouseId!,
      }));
    }
  }, [user?.activeWarehouseId]);

  // Input change handler with automatic error clearing (same as Client Onboarding)
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing (same as Client Onboarding)
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Specialized handlers for specific fields (same pattern as Client Onboarding)
  const handleVehicleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.vehicleFormat(e.target.value);
    handleInputChange('vehicleNumber', sanitized);
  };

  const handleAlphabetsOnly = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.driverNameFormat(e.target.value);
    handleInputChange(field, sanitized);
  };

  const handleMobileChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.digitsOnly(e.target.value);
    if (sanitized.length <= 10) {
      handleInputChange(field, sanitized);
    }
  };

  const handleInvoiceNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.invoiceFormat(e.target.value);
    handleInputChange('invoiceNumber', sanitized);
  };

  const handleEwayBillChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.digitsOnly(e.target.value);
    if (sanitized.length <= 12) {
      handleInputChange('ewayBillNumber', sanitized);
    }
  };

  const handleRemarksChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const sanitized = INPUT_SANITIZERS.remarksFormat(e.target.value);
    handleInputChange('remarks', sanitized);
  };

  // Removed handleShipmentTypeChange - only Inward is supported now

  // Validation functions (identical to Client Onboarding pattern) — each
  // returns the errors it found rather than a boolean, so validateForm can
  // merge them and immediately know (without waiting on state) which field
  // to toast/focus first.
  const validateVehicleAndDriverInfo = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    // Vehicle Number - using FIELD_VALIDATORS (same as Client Onboarding)
    const vehicleError = FIELD_VALIDATORS.vehicleNumber(formData.vehicleNumber, true);
    if (vehicleError) newErrors.vehicleNumber = vehicleError;

    // Driver Name - using enhanced FIELD_VALIDATORS
    const driverNameError = FIELD_VALIDATORS.driverName(formData.driverName, true);
    if (driverNameError) newErrors.driverName = driverNameError;

    // Driver Phone - using FIELD_VALIDATORS (same as Client Onboarding)
    const phoneError = FIELD_VALIDATORS.mobileNumber(formData.driverPhone, true);
    if (phoneError) newErrors.driverPhone = phoneError;

    return newErrors;
  };

  const validateShipmentInfo = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    // Client validation
    if (!formData.clientId || !selectedClient) {
      newErrors.clientId = 'Please search and select a client';
    }

    // Invoice Number - using enhanced FIELD_VALIDATORS (only for inward)
    const invoiceError = FIELD_VALIDATORS.invoiceNumber(formData.invoiceNumber, true);
    if (invoiceError) newErrors.invoiceNumber = invoiceError;

    // E-way Bill Number - optional but must be valid format if provided
    if (formData.ewayBillNumber.trim()) {
      if (!VALIDATION_PATTERNS.NUMBERS_ONLY.test(formData.ewayBillNumber)) {
        newErrors.ewayBillNumber = 'E-way bill number should only contain numbers';
      } else if (formData.ewayBillNumber.length !== 12) {
        newErrors.ewayBillNumber = 'E-way bill number must be exactly 12 digits';
      }
    }

    // Packing Slip Number validation (optional)
    if (formData.packingSlipNumber && formData.packingSlipNumber.length > 50) {
      newErrors.packingSlipNumber = 'Packing slip number must not exceed 50 characters';
    }

    // BE / Inv Date cannot be in the future
    if (formData.invDate && formData.invDate > todayStr()) {
      newErrors.invDate = 'BE / Inv Date cannot be in the future';
    }

    // Remarks validation (optional field)
    const remarksError = FIELD_VALIDATORS.remarks(formData.remarks, false);
    if (remarksError) newErrors.remarks = remarksError;

    return newErrors;
  };

  // Top-to-bottom DOM order of the Inward form's fields — used to pick which
  // invalid field to scroll to and focus first.
  const INWARD_FIELD_ORDER = [
    'vehicleNumber', 'driverName', 'driverPhone',
    'clientId', 'invoiceNumber', 'invDate', 'ewayBillNumber', 'packingSlipNumber',
    'remarks',
  ];

  // Scrolls to and focuses the first field (in visual order) that has an
  // error, so the user isn't left guessing which field needs fixing.
  const focusFirstInvalidField = (fieldErrors: Record<string, string>) => {
    const firstField = INWARD_FIELD_ORDER.find((key) => fieldErrors[key]);
    if (!firstField) return;
    const el = document.getElementById(firstField);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    (el as HTMLInputElement | HTMLTextAreaElement).focus();
  };

  // Main form validation (identical pattern to Client Onboarding handleSubmit)
  const validateForm = (): Record<string, string> => {
    const merged = {
      ...validateVehicleAndDriverInfo(),
      ...validateShipmentInfo(),
    };
    setErrors(merged);
    setError(null);
    return merged;
  };

  // Handle form submission (identical pattern to Client Onboarding)
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate entire form (same as Client Onboarding)
    const fieldErrors = validateForm();
    if (Object.keys(fieldErrors).length > 0) {
      // Surface the first problem as a toast and bring it into view — silently
      // setting per-field error state isn't enough if the field is scrolled
      // out of sight in this long form.
      const firstField = INWARD_FIELD_ORDER.find((key) => fieldErrors[key]);
      showToast(firstField ? fieldErrors[firstField] : 'Please fix the highlighted fields', 'error');
      focusFirstInvalidField(fieldErrors);
      return;
    }

    // Create gate entry payload matching the backend structure (Inward only)
    const gateEntryData = {
      warehouseId: formData.warehouseId,
      vehicleNumber: formData.vehicleNumber.toUpperCase(),
      driverName: formData.driverName,
      driverPhone: formData.driverPhone,
      clientId: formData.clientId,
      shipmentType: 'inward' as 'inward' | 'outward',
      expectedQuantity: formData.expectedQuantity,
      ...(formData.invoiceNumber && { invoiceNumber: formData.invoiceNumber }),
      ...(formData.ewayBillNumber && { ewayBillNumber: formData.ewayBillNumber }),
      ...(formData.packingSlipNumber && { packingSlipNumber: formData.packingSlipNumber }),
      ...(formData.invDate && { invDate: formData.invDate }),
      ...(formData.remarks && { remarks: formData.remarks }),
    };

    createGateEntryMutation.mutate(gateEntryData);
  };
  
  // Vehicle Assignment Form Handlers
  const resetVehicleForm = () => {
    setVehicleFormData({
      vehicleNumber: '',
      driverName: '',
      driverPhone: '',
      remarks: '',
      invDate: todayStr(),
    });
    setVehicleFormErrors({});
  };
  
  const handleVehicleInputChange = (field: string, value: any) => {
    setVehicleFormData(prev => ({ ...prev, [field]: value }));
    if (vehicleFormErrors[field]) {
      setVehicleFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };
  
  const handleVehicleNumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.vehicleFormat(e.target.value);
    handleVehicleInputChange('vehicleNumber', sanitized);
  };
  
  const handleVehicleDriverNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.driverNameFormat(e.target.value);
    handleVehicleInputChange('driverName', sanitized);
  };
  
  const handleVehicleDriverPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.digitsOnly(e.target.value);
    if (sanitized.length <= 10) {
      handleVehicleInputChange('driverPhone', sanitized);
    }
  };
  
  const handleVehicleRemarksChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const sanitized = INPUT_SANITIZERS.remarksFormat(e.target.value);
    handleVehicleInputChange('remarks', sanitized);
  };
  
  const validateVehicleForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    const vehicleError = FIELD_VALIDATORS.vehicleNumber(vehicleFormData.vehicleNumber, true);
    if (vehicleError) newErrors.vehicleNumber = vehicleError;
    
    const driverNameError = FIELD_VALIDATORS.driverName(vehicleFormData.driverName, true);
    if (driverNameError) newErrors.driverName = driverNameError;
    
    const phoneError = FIELD_VALIDATORS.mobileNumber(vehicleFormData.driverPhone, true);
    if (phoneError) newErrors.driverPhone = phoneError;
    
    const remarksError = FIELD_VALIDATORS.remarks(vehicleFormData.remarks, false);
    if (remarksError) newErrors.remarks = remarksError;
    
    setVehicleFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleVehicleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateVehicleForm() || !selectedDispatch) {
      return;
    }
    
    const vehicleData = {
      vehicleNumber: vehicleFormData.vehicleNumber.toUpperCase(),
      driverName: vehicleFormData.driverName,
      driverPhone: vehicleFormData.driverPhone,
      ...(vehicleFormData.remarks && { remarks: vehicleFormData.remarks }),
      ...(vehicleFormData.invDate && { invDate: vehicleFormData.invDate }),
    };
    
    assignVehicleMutation.mutate({
      gateEntryId: selectedDispatch.id,
      vehicleData,
    });
  };
  
  const handleAssignVehicle = (entry: any) => {
    setSelectedDispatch(entry);
    setShowVehicleForm(true);
    resetVehicleForm();
  };
  
  const handleViewOutward = (entry: any) => {
    setViewEntry(entry);
    setShowViewModal(true);
  };

  return (
    <Layout>
      <div className="min-h-screen px-1 py-4 lg:p-4 bg-gradient-to-br from-gray-50 to-gray-100 scrollbar-hide" style={{ overflowY: 'auto' }}>        
        {/* Header with enhanced shadow and blue tint */}
        <div className="mb-4 transform transition-all duration-300 hover:scale-[1.01]">
          <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-brand-100 to-brand-50 p-5 rounded-xl shadow-lg border-2 border-brand-300 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl shadow-md transform transition-transform duration-300 hover:scale-110 hover:rotate-3">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Security Gate Entry</h1>
                <p className="text-gray-600 mt-1">Vehicle entry and document verification system</p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowForm(true);
                resetForm();
              }}
              className="w-full lg:w-auto justify-center px-6 py-3 min-h-[44px] lg:min-h-0 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="whitespace-nowrap">New Gate Entry</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-xl p-6 border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-600">Total Entries</p>
              <div className="w-12 h-12 bg-gradient-to-br from-brand-400 to-brand-500 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
            <p className="text-4xl font-bold text-gray-900">
              {summaryLoading ? (
                <span className="animate-pulse">...</span>
              ) : (
                summary.totalEntries
              )}
            </p>
          </div>
          
          <div className="bg-white rounded-xl p-6 border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-600">Inbound Shipments</p>
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-500 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4" />
                </svg>
              </div>
            </div>
            <p className="text-4xl font-bold text-gray-900">
              {summaryLoading ? (
                <span className="animate-pulse">...</span>
              ) : (
                summary.inwardShipments
              )}
            </p>
          </div>
          
          <div className="bg-white rounded-xl p-6 border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-600">Outbound Shipments</p>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-500 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            </div>
            <p className="text-4xl font-bold text-gray-900">
              {summaryLoading ? (
                <span className="animate-pulse">...</span>
              ) : (
                summary.outwardShipments
              )}
            </p>
          </div>
          
          <div className="bg-white rounded-xl p-6 border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-600">Today's Entries</p>
              <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <p className="text-4xl font-bold text-gray-900">
              {summaryLoading ? (
                <span className="animate-pulse">...</span>
              ) : (
                summary.todaysEntries
              )}
            </p>
          </div>
        </div>

        {/* Gate Entry Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end lg:items-center justify-center z-50 p-3 lg:p-4">
            <div className="bg-white rounded-2xl lg:rounded-lg shadow-2xl max-w-5xl w-full max-h-[85vh] lg:max-h-[95vh] overflow-hidden animate-slide-up lg:animate-none flex flex-col">
              {/* Professional Header */}
              <div className="bg-gradient-to-r from-brand-600 to-brand-700 px-8 py-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="w-8 h-8 text-white mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <div>
                      <h2 className="text-2xl font-bold text-white">New Inbound Entry</h2>
                      <p className="text-brand-100 text-sm mt-1">Register incoming shipment at security gate</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      resetForm();
                    }}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto flex-1 min-h-0 lg:flex-none lg:max-h-[calc(95vh-120px)] bg-gray-50">
                <form onSubmit={handleSubmit} className="p-8 space-y-8">
                  {/* Vehicle & Driver Information */}
                  <div className="bg-white rounded-lg shadow-sm p-8">
                    <div className="flex items-center mb-6">
                      <h2 className="text-xl font-semibold text-gray-900">Vehicle & Driver Information</h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <Input
                        id="vehicleNumber"
                        label="Vehicle Number"
                        value={formData.vehicleNumber}
                        onChange={handleVehicleNumberChange}
                        required
                        error={errors.vehicleNumber}
                        placeholder="e.g., MH12AB1234"
                        maxLength={10}
                      />

                      <Input
                        id="driverName"
                        label="Driver Name"
                        value={formData.driverName}
                        onChange={handleAlphabetsOnly('driverName')}
                        required
                        error={errors.driverName}
                        placeholder="Enter driver name"
                        maxLength={100}
                      />

                      <Input
                        id="driverPhone"
                        label="Driver Phone"
                        value={formData.driverPhone}
                        onChange={handleMobileChange('driverPhone')}
                        type="tel"
                        required
                        error={errors.driverPhone}
                        placeholder="9876543210"
                        maxLength={10}
                      />
                    </div>
                  </div>

                  {/* Shipment Information */}
                  <div className="bg-white rounded-lg shadow-sm p-8">
                    <div className="flex items-center mb-6">
                      <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      <h2 className="text-xl font-semibold text-gray-900">Shipment Information</h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Client <span className="text-red-500">*</span>
                        </label>
                        <div className="relative" ref={clientDropdownRef}>
                          {/* Search Input */}
                          <div className="relative">
                            <input
                              id="clientId"
                              type="text"
                              placeholder={selectedClient ? selectedClient.companyName : "Type to search clients..."}
                              className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:border-transparent ${errors.clientId ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-brand-500'}`}
                              value={selectedClient ? selectedClient.companyName : clientSearchTerm}
                              onChange={selectedClient ? undefined : handleClientSearchChange}
                              onFocus={() => {
                                if (!selectedClient) {
                                  setShowClientDropdown(true);
                                }
                              }}
                              onClick={() => {
                                if (!selectedClient) {
                                  setShowClientDropdown(true);
                                }
                              }}
                              onKeyDown={handleClientSearchKeyDown}
                              readOnly={Boolean(selectedClient)}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center">
                              {selectedClient ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedClient(null);
                                    setFormData({ ...formData, clientId: 0 });
                                    setClientSearchTerm('');
                                    setShowClientDropdown(false);
                                  }}
                                  className="px-2 py-1 text-gray-400 hover:text-gray-600"
                                  title="Clear selection"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              ) : (
                                <div className="px-2 pointer-events-none">
                                  {clientsLoading && debouncedSearchTerm ? (
                                    <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Dropdown Panel */}
                          {showClientDropdown && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                              {/* Client List */}
                              <div className="max-h-48 overflow-y-auto">
                                {clientsLoading ? (
                                  <div className="px-4 py-6 text-sm text-gray-500 text-center">
                                    <div className="flex items-center justify-center">
                                      <svg className="w-5 h-5 text-gray-400 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                      {debouncedSearchTerm ? 'Searching clients...' : 'Loading clients...'}
                                    </div>
                                  </div>
                                ) : clientsError ? (
                                  <div className="px-4 py-6 text-sm text-red-500 text-center">
                                    Error loading clients: {clientsError.message || 'Unknown error'}
                                  </div>
                                ) : clients.length > 0 ? (
                                  <ul className="py-1">
                                    {clients.filter(client => client?.id).map((client: Client) => (
                                      <li key={client.id}>
                                        <button
                                          type="button"
                                          className="w-full text-left px-4 py-3 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-50 last:border-b-0"
                                          onClick={() => handleClientSelect(client)}
                                        >
                                          <div className="font-medium text-gray-900">{client.companyName || 'Unknown Client'}</div>
                                          {client.contactPersonName && (
                                            <div className="text-sm text-gray-600">Contact: {client.contactPersonName}</div>
                                          )}
                                          {client.email && (
                                            <div className="text-xs text-gray-500">{client.email}</div>
                                          )}
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <div className="px-4 py-6 text-sm text-gray-500 text-center">
                                    {debouncedSearchTerm ? `No clients found matching "${debouncedSearchTerm}"` : 'No clients available'}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Selected client confirmation */}
                        {selectedClient && (
                          <div className="mt-2 text-sm text-green-600 flex items-center min-w-0">
                            <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="truncate" title={`Selected: ${selectedClient.companyName}`}>
                              Selected: {selectedClient.companyName}
                            </span>
                          </div>
                        )}
                        {errors.clientId && (
                          <p className="mt-1 text-sm text-red-600">{errors.clientId}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Shipment Type
                        </label>
                        <div className="px-4 py-3 border-2 border-brand-500 bg-brand-50 text-brand-700 rounded-lg">
                          <div className="text-center">
                            <div className="font-medium">Inbound</div>
                            <div className="text-xs">Receiving Goods</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quantity Section - Inward only */}
                    <div className="mt-6">
                      <div className="max-w-md">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Expected Quantity
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder="Number of units/pallets expected to receive (optional)"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                          value={formData.expectedQuantity === 0 ? '' : formData.expectedQuantity}
                          onChange={(e) => {
                            const digitsOnly = INPUT_SANITIZERS.digitsOnly(e.target.value);
                            setFormData({ ...formData, expectedQuantity: digitsOnly === '' ? 0 : parseInt(digitsOnly, 10) });
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Document Verification - Inward only */}
                  <div className="bg-white rounded-lg shadow-sm p-8 border-l-4 border-brand-500">
                    <div className="flex items-center mb-6">
                      <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h2 className="text-xl font-semibold text-gray-900">Inbound Documentation</h2>
                    </div>
                    
                    {/* Inward Documentation Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <Input
                        id="invoiceNumber"
                        label="Invoice Number"
                        value={formData.invoiceNumber}
                        onChange={handleInvoiceNumberChange}
                        required
                        error={errors.invoiceNumber}
                        placeholder="e.g., INV2024001 or ABC123"
                        maxLength={20}
                      />

                      <Input
                        id="invDate"
                        label="BE / Inv Date"
                        type="date"
                        value={formData.invDate}
                        max={todayStr()}
                        error={errors.invDate}
                        onChange={(e) => {
                          if (e.target.value > todayStr()) {
                            showToast('BE / Inv Date cannot be in the future', 'error');
                            return;
                          }
                          handleInputChange('invDate', e.target.value);
                        }}
                      />

                      <Input
                        id="ewayBillNumber"
                        label="E-way Bill Number"
                        value={formData.ewayBillNumber}
                        onChange={handleEwayBillChange}
                        error={errors.ewayBillNumber}
                        placeholder="12-digit E-way bill"
                        maxLength={12}
                      />

                      <Input
                        id="packingSlipNumber"
                        label="Packing Slip Number"
                        value={formData.packingSlipNumber}
                        onChange={(e) => handleInputChange('packingSlipNumber', e.target.value)}
                        error={errors.packingSlipNumber}
                        placeholder="Packing slip number"
                        maxLength={50}
                      />
                    </div>
                  </div>

                  {/* Additional Notes */}
                  <div className="bg-white rounded-lg shadow-sm p-8">
                    <div className="flex items-center mb-6">
                      <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                      </svg>
                      <h2 className="text-xl font-semibold text-gray-900">Additional Notes</h2>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Remarks & Special Instructions
                        <span className="text-gray-500 text-xs ml-2">
                          ({formData.remarks.length}/200 characters)
                        </span>
                      </label>
                      <textarea
                        id="remarks"
                        rows={4}
                        placeholder="Letters, numbers, and spaces only. Max 200 characters."
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent ${
                          errors.remarks ? 'border-red-500' : 'border-gray-300'
                        }`}
                        value={formData.remarks}
                        onChange={handleRemarksChange}
                        maxLength={200}
                      />
                      {errors.remarks && (
                        <p className="mt-1 text-sm text-red-600">{errors.remarks}</p>
                      )}
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-red-800">{error}</p>
                      </div>
                    </div>
                  )}

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false);
                        setError(null);
                      }}
                      className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
                      disabled={createGateEntryMutation.isPending}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 text-white rounded-lg focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center bg-brand-600 hover:bg-brand-700 focus:ring-brand-500"
                      disabled={createGateEntryMutation.isPending || clientsLoading}
                    >
                      {createGateEntryMutation.isPending ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Creating Entry...
                        </>
                      ) : (
                        'Create Inbound Entry'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Gate Entries List */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:border-primary-300">
          <div className="p-5 bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-300">
            <div className="flex items-center">
              <svg className="w-6 h-6 text-primary-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900">
                Gate Entries
                {gateEntries.length > 0 && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({gateEntries.length} {gateEntries.length === 1 ? 'entry' : 'entries'})
                  </span>
                )}
              </h3>
            </div>
          </div>

          <div className="px-1 py-3 lg:p-5">
            <ResponsiveTable
              columns={[
                {
                  key: 'entryNumber',
                  label: 'Gate Entry #',
                  width: '150px',
                  render: (_, entry) => (
                    <div className="text-sm font-semibold text-gray-900">
                      {entry.entryNumber || entry.gateEntryNumber || 
                       `${entry.shipmentType === 'outward' ? 'GOUT' : 'GIN'}-${new Date().getFullYear()}-${String(entry.id).padStart(5, '0')}`}
                    </div>
                  )
                },
                {
                  key: 'createdAt',
                  label: 'Date & Time',
                  width: '140px',
                  render: (_, entry) => (
                    <div>
                      <div className="text-sm text-gray-900">{new Date(entry.createdAt || entry.entryDate).toLocaleDateString()}</div>
                      <div className="text-xs text-gray-500">{new Date(entry.createdAt || `${entry.entryDate} ${entry.entryTime}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                  )
                },
                {
                  key: 'vehicleNumber',
                  label: 'Vehicle / Driver',
                  width: '180px',
                  render: (_, entry) => {
                    // Check if vehicle is pending assignment (placeholder values)
                    const isPending = entry.vehicleNumber?.startsWith('PENDING') || 
                                     entry.driverName === 'PENDING ASSIGNMENT' ||
                                     !entry.vehicleNumber;
                    
                    if (isPending && entry.shipmentType === 'outward') {
                      return (
                        <div>
                          <div className="text-sm font-medium text-gray-400 italic">Not Assigned</div>
                          <div className="text-xs text-gray-400">Awaiting Assignment</div>
                        </div>
                      );
                    }
                    
                    return (
                      <div>
                        <div className="text-sm font-medium text-gray-900">{entry.vehicleNumber || '-'}</div>
                        <div className="text-xs text-gray-500">{entry.driverName || '-'}</div>
                      </div>
                    );
                  }
                },
                {
                  key: 'clientName',
                  label: 'Client',
                  width: '200px',
                  render: (_, entry) => (
                    <div className="text-sm text-gray-900 max-w-[200px] truncate" title={entry.client?.companyName || entry.clientName || ''}>
                      {entry.client?.companyName || entry.clientName || '-'}
                    </div>
                  )
                },
                {
                  key: 'shipmentType',
                  label: 'Type',
                  width: '120px',
                  render: (_, entry) => (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      entry.shipmentType === 'inward' 
                        ? 'bg-green-100 text-green-800 ring-1 ring-green-200' 
                        : 'bg-purple-100 text-purple-800 ring-1 ring-purple-200'
                    }`}>
                      {entry.shipmentType}
                    </span>
                  )
                },
                {
                  key: 'expectedQuantity',
                  label: 'Qty',
                  width: '80px',
                  render: (_, entry) => (
                    <div className="text-sm font-semibold text-gray-900">{entry.expectedQuantity}</div>
                  )
                },
                {
                  key: 'status',
                  label: 'Status',
                  width: '180px',
                  render: (_, entry) => {
                    const statusMap: Record<string, { label: string; className: string }> = {
                      pending_dock_assignment: { label: 'Pending Dock Assignment', className: 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200' },
                      dock_assigned: { label: 'Dock Assigned', className: 'bg-brand-100 text-brand-800 ring-1 ring-brand-200' },
                      grn_created: { label: 'Inbound Done', className: 'bg-green-100 text-green-800 ring-1 ring-green-200' },
                      completed: { label: 'Inbound Done', className: 'bg-green-100 text-green-800 ring-1 ring-green-200' },
                      pick_slip_created: { label: 'Pick Slip Created', className: 'bg-brand-100 text-brand-800 ring-1 ring-brand-200' },
                      dispatched: { label: 'Outbound Done', className: 'bg-green-100 text-green-800 ring-1 ring-green-200' },
                      cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800 ring-1 ring-red-200' },
                    };
                    const info = statusMap[entry.status] || { label: 'Pending Dock Assignment', className: 'bg-gray-100 text-gray-800 ring-1 ring-gray-200' };
                    return (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${info.className}`}>
                        {info.label}
                      </span>
                    );
                  }
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  width: '180px',
                  render: (_, entry) => (
                    <div>
                      {entry.shipmentType === 'inward' ? (
                        <div className="flex gap-1">
                          <button
                            className="bg-brand-600 hover:bg-brand-700 text-white px-3 py-1 rounded-lg text-xs font-medium inline-flex items-center gap-1"
                            onClick={() => { setViewInwardEntry(entry); setShowInwardModal(true); }}
                            title="View Transport Details"
                          >
                            <i className="fas fa-eye"></i>
                            View
                          </button>
                          <button
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg text-xs font-medium inline-flex items-center gap-1"
                            onClick={() => navigate('/inward', { state: { selectedEntry: entry, message: `Viewing gate entry: ${entry.entryNumber || entry.gateEntryNumber || entry.id}` } })}
                            title="Go to Inbound Module"
                          >
                            <i className="fas fa-arrow-down"></i>
                            Inbound
                          </button>
                        </div>
                      ) : entry.shipmentType === 'outward' ? (
                        // Check if vehicle is already assigned
                        entry.vehicleNumber && !entry.vehicleNumber.startsWith('PENDING') ? (
                          <button 
                            className="bg-brand-600 hover:bg-brand-700 text-white px-3 py-1 rounded-lg text-xs font-medium inline-flex items-center gap-1"
                            onClick={() => handleViewOutward(entry)}
                            title="View Outbound Details"
                          >
                            <i className="fas fa-eye"></i>
                            View
                          </button>
                        ) : (
                          <button 
                            className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 rounded-lg text-xs font-medium inline-flex items-center gap-1"
                            onClick={() => handleAssignVehicle(entry)}
                            title="Assign Vehicle for Outbound"
                          >
                            <i className="fas fa-truck"></i>
                            Assign Vehicle
                          </button>
                        )
                      ) : (
                        <span className="text-gray-400 text-xs">No action</span>
                      )}
                    </div>
                  )
                }
              ]}
              data={gateEntries}
              isLoading={isFetchingNextPage}
              onLoadMore={() => fetchNextPage()}
              hasMore={hasNextPage}
              height="600px"
              emptyMessage="No gate entries found. New entries will appear here."
            />
            
            {/* Error State - shown outside table */}
            {gateEntriesError && (
              <div className="text-center py-8 bg-gradient-to-br from-red-50 to-red-100 rounded-xl mt-4">
                <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Failed to Load Gate Entries</h3>
                <p className="text-gray-600 mb-4">There was an error loading the gate entries.</p>
                <button
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['gateEntries'] })}
                  className="px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-300 font-semibold shadow-lg"
                >
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Retry Loading
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Vehicle Assignment Form Modal */}
      {showVehicleForm && selectedDispatch && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end lg:items-center justify-center z-50 p-3 lg:p-4">
          <div className="bg-white rounded-2xl lg:rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] lg:max-h-[95vh] overflow-hidden animate-slide-up lg:animate-none flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-brand-600 to-brand-700 px-8 py-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-8 h-8 text-white mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2v0a2 2 0 01-2-2v-2a2 2 0 00-2-2H8z" />
                  </svg>
                  <div>
                    <h2 className="text-2xl font-bold text-white">🚚 Assign Vehicle</h2>
                    <p className="text-brand-100 text-sm mt-1">
                      Gate Entry: {selectedDispatch.entryNumber || selectedDispatch.gateEntryNumber || `#${selectedDispatch.id}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowVehicleForm(false);
                    setSelectedDispatch(null);
                    resetVehicleForm();
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 min-h-0 lg:flex-none lg:max-h-[calc(95vh-120px)] bg-gray-50">
              <form onSubmit={handleVehicleFormSubmit} className="p-8 space-y-6">
                {/* Gate Entry Information */}
                <div className="bg-brand-50 border-l-4 border-brand-500 p-4 rounded-r-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-brand-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-brand-900">
                        Client: <span className="font-bold">{selectedDispatch.client?.companyName || selectedDispatch.clientName || 'N/A'}</span>
                      </p>
                      <p className="text-sm text-brand-800 mt-1">
                        Entry Date: {new Date(selectedDispatch.createdAt || selectedDispatch.entryDate).toLocaleString()}
                      </p>
                      {selectedDispatch.expectedQuantity && (
                        <p className="text-sm text-brand-800 mt-1">
                          Expected Quantity: {selectedDispatch.expectedQuantity}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Vehicle & Driver Information */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center mb-4">
                    <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2v0a2 2 0 01-2-2v-2a2 2 0 00-2-2H8z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-900">Vehicle & Driver Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Input
                      label="Vehicle Number"
                      value={vehicleFormData.vehicleNumber}
                      onChange={handleVehicleNumChange}
                      required
                      error={vehicleFormErrors.vehicleNumber}
                      placeholder="e.g., MH12AB1234"
                      maxLength={20}
                    />

                    <Input
                      label="Driver Name"
                      value={vehicleFormData.driverName}
                      onChange={handleVehicleDriverNameChange}
                      required
                      error={vehicleFormErrors.driverName}
                      placeholder="Enter driver name"
                      maxLength={100}
                    />

                    <Input
                      label="Driver Phone"
                      value={vehicleFormData.driverPhone}
                      onChange={handleVehicleDriverPhoneChange}
                      type="tel"
                      required
                      error={vehicleFormErrors.driverPhone}
                      placeholder="9876543210"
                      maxLength={10}
                    />

                    <Input
                      label="Inv Date"
                      type="date"
                      value={vehicleFormData.invDate}
                      max={todayStr()}
                      onChange={(e) => {
                        if (e.target.value > todayStr()) {
                          showToast('Inv Date cannot be in the future', 'error');
                          return;
                        }
                        setVehicleFormData(prev => ({ ...prev, invDate: e.target.value }));
                      }}
                    />
                  </div>
                </div>

                {/* Additional Information */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center mb-4">
                    <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Remarks & Special Instructions
                      <span className="text-gray-500 text-xs ml-2">
                        ({vehicleFormData.remarks.length}/200 characters)
                      </span>
                    </label>
                    <textarea
                      rows={4}
                      placeholder="Letters, numbers, and spaces only. Max 200 characters."
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent ${
                        vehicleFormErrors.remarks ? 'border-red-500' : 'border-gray-300'
                      }`}
                      value={vehicleFormData.remarks}
                      onChange={handleVehicleRemarksChange}
                      maxLength={200}
                    />
                    {vehicleFormErrors.remarks && (
                      <p className="mt-1 text-sm text-red-600">{vehicleFormErrors.remarks}</p>
                    )}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowVehicleForm(false);
                      setSelectedDispatch(null);
                      resetVehicleForm();
                    }}
                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
                    disabled={assignVehicleMutation.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 text-white bg-brand-600 hover:bg-brand-700 rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                    disabled={assignVehicleMutation.isPending}
                  >
                    {assignVehicleMutation.isPending ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Assigning Vehicle...
                      </>
                    ) : (
                      <>
                        🚚 Assign Vehicle & Complete
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* View Outward Details Modal */}
      {showViewModal && viewEntry && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end lg:items-center justify-center z-50 p-3 lg:p-4">
          <div className="bg-white rounded-2xl lg:rounded-lg shadow-2xl max-w-4xl w-full max-h-[85vh] lg:max-h-[95vh] overflow-hidden animate-slide-up lg:animate-none flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-brand-600 to-brand-700 px-8 py-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-8 h-8 text-white mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Outbound Entry Details</h2>
                    <p className="text-brand-100 text-sm mt-1">
                      Gate Entry: {viewEntry.entryNumber || viewEntry.gateEntryNumber || `#${viewEntry.id}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewEntry(null);
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 min-h-0 lg:flex-none lg:max-h-[calc(95vh-120px)] bg-gray-50 p-8 space-y-6">
              {/* Entry Information */}
              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-brand-500">
                <div className="flex items-center mb-4">
                  <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-900">Entry Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Entry Number</label>
                    <p className="text-base font-semibold text-gray-900">
                      {viewEntry.entryNumber || viewEntry.gateEntryNumber || `GOUT-${new Date().getFullYear()}-${String(viewEntry.id).padStart(5, '0')}`}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Entry Date & Time</label>
                    <p className="text-base text-gray-900">
                      {new Date(viewEntry.createdAt || viewEntry.entryDate).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Client</label>
                    <p className="text-base font-semibold text-gray-900">
                      {viewEntry.client?.companyName || viewEntry.clientName || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Shipment Type</label>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 ring-1 ring-purple-200">
                      Outbound
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Expected Quantity</label>
                    <p className="text-base font-semibold text-gray-900">{viewEntry.expectedQuantity || 0}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                    {(() => {
                      const statusMap: Record<string, { label: string; className: string }> = {
                        pending_dock_assignment: { label: 'Pending Dock Assignment', className: 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200' },
                        dock_assigned: { label: 'Dock Assigned', className: 'bg-brand-100 text-brand-800 ring-1 ring-brand-200' },
                        grn_created: { label: 'Inbound Done', className: 'bg-green-100 text-green-800 ring-1 ring-green-200' },
                        completed: { label: 'Inbound Done', className: 'bg-green-100 text-green-800 ring-1 ring-green-200' },
                        pick_slip_created: { label: 'Pick Slip Created', className: 'bg-brand-100 text-brand-800 ring-1 ring-brand-200' },
                        dispatched: { label: 'Outbound Done', className: 'bg-green-100 text-green-800 ring-1 ring-green-200' },
                        cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800 ring-1 ring-red-200' },
                      };
                      const info = statusMap[viewEntry.status] || { label: 'Pending Dock Assignment', className: 'bg-gray-100 text-gray-800 ring-1 ring-gray-200' };
                      return (
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${info.className}`}>
                          {info.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Vehicle & Driver Information */}
              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-green-500">
                <div className="flex items-center mb-4">
                  <svg className="w-6 h-6 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2v0a2 2 0 01-2-2v-2a2 2 0 00-2-2H8z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-900">Vehicle & Driver Details</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Vehicle Number</label>
                    <p className="text-base font-semibold text-gray-900">{viewEntry.vehicleNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Driver Name</label>
                    <p className="text-base text-gray-900">{viewEntry.driverName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Driver Phone</label>
                    <p className="text-base text-gray-900">{viewEntry.driverPhone || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Document Information */}
              {(viewEntry.invoiceNumber || viewEntry.ewayBillNumber || viewEntry.packingSlipNumber) && (
                <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-purple-500">
                  <div className="flex items-center mb-4">
                    <svg className="w-6 h-6 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-900">Document Information</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {viewEntry.invoiceNumber && (
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Invoice Number</label>
                        <p className="text-base text-gray-900">{viewEntry.invoiceNumber}</p>
                      </div>
                    )}
                    {viewEntry.ewayBillNumber && (
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">E-way Bill Number</label>
                        <p className="text-base text-gray-900">{viewEntry.ewayBillNumber}</p>
                      </div>
                    )}
                    {viewEntry.packingSlipNumber && (
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Gate Pass Number</label>
                        <p className="text-base text-gray-900">{viewEntry.packingSlipNumber}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Outward Products */}
              {viewEntry.dispatchId && (
                <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-orange-500">
                  <div className="flex items-center mb-4">
                    <svg className="w-6 h-6 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-900">Products Outbound</h3>
                  </div>
                  {viewItemsLoading ? (
                    <p className="text-sm text-gray-500">Loading items…</p>
                  ) : !viewDispatchItems || viewDispatchItems.length === 0 ? (
                    <p className="text-sm text-gray-500">No items recorded for this outward.</p>
                  ) : (
                    <div className="space-y-3">
                      {viewDispatchItems.map((item: any) => {
                        const isShared = (item.palletCount ?? 0) === 0;
                        const total = (item.goodQty || 0) + (item.cdamQty || 0) + (item.damQty || 0);
                        const storage = item.storageSelections || item.storage || [];
                        return (
                          <div key={item.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div className="flex items-start justify-between mb-3">
                              <div className="min-w-0">
                                <p className="text-base font-semibold text-gray-900 break-words">{item.productDescription}</p>
                                <p className="text-xs text-gray-600 mt-1">Batch: <span className="font-medium">{item.batchNo || '-'}</span></p>
                              </div>
                              {isShared && (
                                <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 ring-1 ring-amber-200 whitespace-nowrap">
                                  Shared Pallet
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                              <div>
                                <p className="text-xs text-gray-500">Good</p>
                                <p className="font-semibold text-green-700">{item.goodQty || 0}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">CDAM</p>
                                <p className="font-semibold text-yellow-700">{item.cdamQty || 0}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">DAM</p>
                                <p className="font-semibold text-red-700">{item.damQty || 0}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Total Qty</p>
                                <p className="font-bold text-gray-900">{total}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Pallets</p>
                                <p className="font-semibold text-brand-700">{item.palletCount || 0}</p>
                              </div>
                            </div>
                            {storage.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <p className="text-xs font-medium text-gray-600 mb-2">Storage Source</p>
                                <div className="space-y-1">
                                  {storage.map((s: any, idx: number) => (
                                    <p key={idx} className="text-xs text-gray-700">
                                      {[s.zone, s.rack, s.location].filter(Boolean).join(' / ') || '-'}
                                      {s.palletsToDispatch ? <span className="text-gray-500"> — {s.palletsToDispatch} pallet(s)</span> : null}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            )}
                            {item.remarks && (
                              <p className="text-xs text-gray-500 mt-2 italic">Note: {item.remarks}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Remarks */}
              {viewEntry.remarks && (
                <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-gray-500">
                  <div className="flex items-center mb-4">
                    <svg className="w-6 h-6 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-900">Remarks</h3>
                  </div>
                  <p className="text-base text-gray-700 whitespace-pre-wrap">{viewEntry.remarks}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewEntry(null);
                  }}
                  className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    navigate('/outward', { 
                      state: { 
                        selectedEntry: viewEntry,
                        message: `Viewing outward gate entry: ${viewEntry.entryNumber || viewEntry.gateEntryNumber || viewEntry.id}`
                      }
                    });
                  }}
                  className="px-6 py-2 text-white bg-brand-600 hover:bg-brand-700 rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                  Go to Outbound Module
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Inward Transport Details Modal */}
      {showInwardModal && viewInwardEntry && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-5 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <div>
                  <h2 className="text-xl font-bold">Inbound Transport Details</h2>
                  <p className="text-green-100 text-sm mt-0.5">Gate Entry: {viewInwardEntry.entryNumber || viewInwardEntry.gateEntryNumber || `#${viewInwardEntry.id}`}</p>
                </div>
              </div>
              <button onClick={() => { setShowInwardModal(false); setViewInwardEntry(null); }} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6 space-y-5 bg-gray-50">
              {/* Entry Info */}
              <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-green-500">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Entry Information</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Client',     value: viewInwardEntry.client?.companyName || viewInwardEntry.clientName },
                    { label: 'Entry Date', value: new Date(viewInwardEntry.createdAt || viewInwardEntry.entryDate).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) },
                    { label: 'Expected Qty', value: viewInwardEntry.expectedQuantity },
                    { label: 'Status',     value: viewInwardEntry.status?.replace(/_/g, ' ') },
                  ].map(f => (
                    <div key={f.label}>
                      <p className="text-xs text-gray-500 font-medium mb-0.5">{f.label}</p>
                      <p className="text-sm font-semibold text-gray-900 capitalize">{f.value || '—'}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Transport Details */}
              <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-orange-400">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Transport Details</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { label: 'Vehicle Number', value: viewInwardEntry.vehicleNumber, mono: true },
                    { label: 'Driver Name',    value: viewInwardEntry.driverName },
                    { label: 'Driver Phone',   value: viewInwardEntry.driverPhone },
                  ].map(f => (
                    <div key={f.label} className="rounded-lg bg-gray-50 border border-gray-100 p-3">
                      <p className="text-xs text-gray-400 font-semibold uppercase mb-1">{f.label}</p>
                      <p className={`text-sm text-gray-900 ${f.mono ? 'font-mono font-bold' : ''}`}>{f.value || <span className="text-gray-300 italic">—</span>}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Documents */}
              {(viewInwardEntry.invoiceNumber || viewInwardEntry.ewayBillNumber || viewInwardEntry.packingSlipNumber) && (
                <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-purple-400">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Documents</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Invoice Number',      value: viewInwardEntry.invoiceNumber },
                      { label: 'E-Way Bill Number',   value: viewInwardEntry.ewayBillNumber },
                      { label: 'Packing Slip Number', value: viewInwardEntry.packingSlipNumber },
                    ].filter(f => f.value).map(f => (
                      <div key={f.label} className="rounded-lg bg-gray-50 border border-gray-100 p-3">
                        <p className="text-xs text-gray-400 font-semibold uppercase mb-1">{f.label}</p>
                        <p className="text-sm font-mono font-semibold text-gray-900">{f.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewInwardEntry.remarks && (
                <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-gray-300">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Remarks</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{viewInwardEntry.remarks}</p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-white border-t border-gray-100 flex justify-between">
              <button onClick={() => { setShowInwardModal(false); setViewInwardEntry(null); }} className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                Close
              </button>
              <button
                onClick={() => { setShowInwardModal(false); navigate('/inward', { state: { selectedEntry: viewInwardEntry } }); }}
                className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium inline-flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                Go to Inbound Module
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};